import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Calculate SHA-256 checksum
async function calculateChecksum(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

const CreateSubmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  // Submission type selection
  const [submissionType, setSubmissionType] = useState<'via_agency' | 'direct_to_head'>('via_agency');

  // Original asset upload
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'confirmed' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [storageKey, setStorageKey] = useState<string | null>(null);

  // Creative fields removed - now handled via Creative tab on detail page

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setUploadStatus('idle');

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else if (selectedFile.type.startsWith('video/')) {
      const videoUrl = URL.createObjectURL(selectedFile);
      setFilePreview(videoUrl);
    } else {
      setFilePreview(null);
    }
  };

  const createDraftSubmission = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/submissions`,
        {
          title: formData.title,
          description: formData.description,
          submission_type: submissionType
        }
      );
      return response.data.id;
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to create submission');
    }
  };

  const uploadFileToS3 = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in title and description');
      return;
    }

    setUploadStatus('uploading');
    setError(null);
    setUploadProgress(10);

    try {
      // Step 1: Create DRAFT submission
      setUploadProgress(20);
      const newSubmissionId = await createDraftSubmission();
      setSubmissionId(newSubmissionId);
      
      // Step 2: Upload via backend proxy (bypasses CORS)
      setUploadProgress(30);
      const formDataToSend = new FormData();
      formDataToSend.append('files', file); // Changed from 'file' to 'files' to match backend
      
      await axios.post(
        `${API_URL}/api/v1/submissions/${newSubmissionId}/asset/upload`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = 30 + Math.round((progressEvent.loaded / progressEvent.total) * 65);
              setUploadProgress(progress);
            }
          }
        }
      );
      
      setUploadProgress(100);
      setUploadStatus('confirmed');
      
      // Redirect to submission detail after 2 seconds
      setTimeout(() => {
        navigate(`/submissions/${newSubmissionId}`);
      }, 2000);

    } catch (err: any) {
      console.error('Upload error:', err);
      
      // Handle different error formats
      let errorMessage = 'Upload failed';
      if (err.response?.data?.detail) {
        // FastAPI validation error - could be string or array
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map((e: any) => 
            typeof e === 'string' ? e : (e.msg || JSON.stringify(e))
          ).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setUploadStatus('error');
      setUploadProgress(0);
    }
  };

  const canSubmit = () => {
    if (uploadStatus !== 'idle') return false;
    if (!file || !formData.title.trim() || !formData.description.trim()) return false;
    return true;
  };

  const handleSubmit = () => {
    // Both submission types now save as DRAFT first
    uploadFileToS3();
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate('/submissions')}
            className="text-blue-600 hover:text-blue-800 mb-3 sm:mb-4 inline-block"
          >
            ← Back to Submissions
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create New Submission</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            Upload your content and save as draft. You can add creative assets and submit later.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter submission title"
              disabled={uploadStatus === 'uploading' || uploadStatus === 'confirmed'}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your submission"
              disabled={uploadStatus === 'uploading' || uploadStatus === 'confirmed'}
            />
          </div>

          {/* Submission Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Submission Type <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <label
                className={`flex-1 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  submissionType === 'via_agency'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="submissionType"
                  value="via_agency"
                  checked={submissionType === 'via_agency'}
                  onChange={(e) => setSubmissionType(e.target.value as 'via_agency' | 'direct_to_head')}
                  className="sr-only"
                  disabled={uploadStatus === 'uploading' || uploadStatus === 'confirmed'}
                />
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        submissionType === 'via_agency'
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {submissionType === 'via_agency' && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">Via Agency</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Submit original asset to Creative Agency for review and packaging
                    </p>
                  </div>
                </div>
              </label>

              <label
                className={`flex-1 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  submissionType === 'direct_to_head'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="submissionType"
                  value="direct_to_head"
                  checked={submissionType === 'direct_to_head'}
                  onChange={(e) => setSubmissionType(e.target.value as 'via_agency' | 'direct_to_head')}
                  className="sr-only"
                  disabled={uploadStatus === 'uploading' || uploadStatus === 'confirmed'}
                />
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        submissionType === 'direct_to_head'
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {submissionType === 'direct_to_head' && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">Direct to Head</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Upload both original and final creative, submit directly to Marketing Head
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File <span className="text-red-500">*</span>
            </label>
            
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {!file ? (
                <div>
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Supported: Images, Videos, Audio (MP3, WAV), PDF, ZIP files
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                  </p>
                  {filePreview && file.type.startsWith('image/') && (
                    <img src={filePreview} alt="Preview" className="mt-4 max-h-48 mx-auto rounded" />
                  )}
                  {filePreview && file.type.startsWith('video/') && (
                    <video src={filePreview} className="mt-4 max-h-48 mx-auto rounded" controls />
                  )}
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              className="hidden"
              accept="image/*,video/*,audio/*,application/pdf,application/zip"
              disabled={uploadStatus === 'uploading' || uploadStatus === 'confirmed'}
            />
          </div>

          {/* Creative section removed - will be added via Creative tab on detail page */}

          {/* Upload Progress */}
          {uploadStatus === 'uploading' && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {uploadStatus === 'confirmed' && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-green-800 font-medium">✓ Upload successful!</p>
              <p className="text-green-700 text-sm mt-1">Redirecting to submission details...</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit()}
              className={`flex-1 py-3 px-6 rounded-md font-medium transition-colors ${
                canSubmit()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {uploadStatus === 'uploading' 
                ? 'Uploading...' 
                : 'Upload & Save as Draft'}
            </button>
            
            <button
              onClick={() => navigate('/submissions')}
              className="px-6 py-3 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50"
              disabled={uploadStatus === 'uploading'}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSubmissionPage;
